import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Eraser, Check, Pen, FileSignature, AlertCircle } from 'lucide-react';
import { useTranslation } from '../i18n';

/**
 * SignaturePad Component
 * A canvas-based signature pad for capturing electronic signatures.
 * 
 * Features:
 * - Draw signature with mouse or touch
 * - Clear and redo functionality
 * - Type signature option
 * - Returns base64 PNG data
 */

export function SignaturePad({
  open,
  onOpenChange,
  onSign,
  isLoading = false,
  signerName = '',
  contractTitle = ''
}) {
  const [canvasElement, setCanvasElement] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [name, setName] = useState(signerName);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');

  // Initialize canvas
  useEffect(() => {
    if (!open || signatureMode !== 'draw' || !canvasElement) return;

    let animationFrameId;

    const initCanvas = () => {
      // Avoid initializing when hidden or still sizing
      if (canvasElement.offsetWidth === 0 || canvasElement.offsetHeight === 0) return;

      const ctx = canvasElement.getContext('2d');

      // Set canvas size
      canvasElement.width = canvasElement.offsetWidth * 2;
      canvasElement.height = canvasElement.offsetHeight * 2;
      ctx.scale(2, 2);

      // Set drawing style
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Clear canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);

      setHasDrawn(false);
    };

    // Use ResizeObserver to reliably match responsive and animated dialogs
    const resizeObserver = new ResizeObserver(() => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        initCanvas();
      });
    });

    resizeObserver.observe(canvasElement);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [open, signatureMode, canvasElement]);

  const getCoordinates = (e) => {
    if (!canvasElement) return { x: 0, y: 0 };
    const rect = canvasElement.getBoundingClientRect();

    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    if (!canvasElement) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasElement.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    if (!canvasElement) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasElement.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!canvasElement) return;
    const ctx = canvasElement.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);
    setHasDrawn(false);
  };

  const generateTypedSignature = () => {
    if (!typedSignature.trim()) return null;

    // Create a canvas for the typed signature
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the typed signature in a handwriting-like font
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'italic 48px "Brush Script MT", cursive, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    let signatureData;

    if (signatureMode === 'draw') {
      if (!hasDrawn || !canvasElement) return;
      signatureData = canvasElement.toDataURL('image/png');
    } else {
      signatureData = generateTypedSignature();
      if (!signatureData) return;
    }

    onSign({
      signature_data: signatureData,
      signer_name: name.trim()
    });
  };

  const isValid = name.trim() && (
    (signatureMode === 'draw' && hasDrawn) ||
    (signatureMode === 'type' && typedSignature.trim())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="signature-dialog">
        <DialogHeader className="p-6 pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            Sign Contract
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {contractTitle ? `Signing: ${contractTitle}` : 'Add your electronic signature to complete the contract.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-2 min-h-0">
          <div className="space-y-4 py-2">
            {/* Signer Name */}
            <div className="space-y-2">
              <Label htmlFor="signer-name">Full Legal Name</Label>
              <Input
                id="signer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                data-testid="signer-name-input"
              />
            </div>

            {/* Signature Mode Toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={signatureMode === 'draw' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSignatureMode('draw');
                  setTypedSignature('');
                }}
                className="flex-1"
              >
                <Pen className="h-4 w-4 mr-2" />
                Draw Signature
              </Button>
              <Button
                type="button"
                variant={signatureMode === 'type' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSignatureMode('type');
                  setHasDrawn(false);
                }}
                className="flex-1"
              >
                <FileSignature className="h-4 w-4 mr-2" />
                Type Signature
              </Button>
            </div>

            {/* Signature Input Area & Controls Wrapper */}
            <div className="min-h-[216px] flex flex-col space-y-4">
              {/* Signature Input Area */}
              {signatureMode === 'draw' ? (
                <Card className="border-2 border-dashed flex-shrink-0">
                  <CardContent className="p-0">
                    <canvas
                      ref={setCanvasElement}
                      className="w-full h-40 min-h-[140px] cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      data-testid="signature-canvas"
                    />
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2 flex-shrink-0">
                  <Input
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your signature"
                    className="text-2xl italic font-serif h-20 text-center"
                    data-testid="typed-signature-input"
                  />
                  {typedSignature && (
                    <Card className="bg-muted/30">
                      <CardContent className="py-4 text-center">
                        <p className="text-3xl italic font-serif text-foreground">{typedSignature}</p>
                        <p className="text-xs text-muted-foreground mt-2">Signature Preview</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Clear Button for Draw Mode */}
              {signatureMode === 'draw' && (
                <div className="flex justify-end flex-shrink-0 mt-auto">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearCanvas}
                  >
                    <Eraser className="h-4 w-4 mr-2" />
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* Legal Notice */}
            <Alert className="bg-muted/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                By signing, you agree that this electronic signature is legally binding and has the same
                legal effect as a handwritten signature. Your signature, name, date, and IP address will
                be recorded for verification purposes.
              </AlertDescription>
            </Alert>
          </div>
        </div>

        <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
          <DialogFooter className="pt-2 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              data-testid="submit-signature-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Sign Contract
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SignaturePad;
